'use client'

import { useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import { XIcon, WarningCircleIcon } from '@phosphor-icons/react'
import {
  DateField,
  MultiSelectProperty,
  PROPERTY_ICONS,
  InlinePropertyPopover,
} from '@/ui/property-fields'
import { TopicSubtitleSelector } from './forms/TopicSubtitleSelector'
import { SuggestedPeopleChips } from './form-sections'
import { ICON_SIZES, FOCUS_STYLES, COMPACT_CLASSES } from '@/ui/design-tokens'
import { cn } from '@/lib/tailwind'
import { ENTRY_PRESENTATION } from '@/entries'

interface CaptureChipsRowProps {
  /** Current entry type */
  effectiveType: 'task' | 'journal' | null

  /** Toggles between task and journal type */
  onTypeToggle: () => void

  /** Due date (task mode only) */
  dueDate: Date | null
  onDueDateChange: (date: Date | null) => void

  /** Importance toggle (task mode only) */
  important: boolean
  onImportantChange: (important: boolean) => void

  /** Topics */
  selectedTopicIds: string[]
  availableTopics: Array<{ id: string; name: string }>
  onTopicsChange: (topicIds: string[]) => void
  onAddTopic: () => void
  onCreateTopic?: (name: string) => void

  /** People */
  selectedPeople: Array<{ id: string; title: string }>
  availablePeople: Array<{ id: string; title: string }>
  onAddPerson: (person: { id: string; title: string }) => void
  onRemovePerson: (person: { id: string; title: string }) => void
  isPeopleLoading: boolean
  onAddNewPerson: () => void

  /** Suggested people from AI */
  suggestedPeople: string[]
  addingPerson: string | null
  onAddSuggestedPerson: (name: string) => void
  onDismissSuggestedPerson: (name: string) => void

  /** Project selector */
  selectedProject: { id: string; name: string } | null
  projectOptions: Array<{ value: string; label: string }>
  onProjectChange: (value: string | null) => void

  /** When true, project is shown as a static badge and cannot be changed */
  projectLocked?: boolean

  /** Disabled state */
  disabled: boolean

  /** Fields updated by AI analysis — triggers flash animation */
  aiUpdatedFields: Set<string>

  /** Controls AnimatePresence visibility */
  show: boolean
}

/**
 * FlashChip — wraps a chip in a brief border-accent flash when the field
 * appears in aiUpdatedFields. Fades after 300ms.
 */
function FlashWrapper({
  fieldKey,
  aiUpdatedFields,
  children,
}: {
  fieldKey: string
  aiUpdatedFields: Set<string>
  children: React.ReactNode
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const flashedRef = useRef(false)

  useEffect(() => {
    if (aiUpdatedFields.has(fieldKey) && !flashedRef.current && ref.current) {
      flashedRef.current = true
      const el = ref.current
      el.classList.add('ring-1', 'ring-accent', 'rounded-full')
      const timer = setTimeout(() => {
        el.classList.remove('ring-1', 'ring-accent', 'rounded-full')
      }, 600)
      return () => clearTimeout(timer)
    }
  }, [fieldKey, aiUpdatedFields])

  return (
    <span ref={ref} className="transition-shadow duration-300">
      {children}
    </span>
  )
}

/**
 * TypeChip — clickable chip that shows current entry type and toggles on click.
 */
function TypeChip({
  effectiveType,
  onToggle,
  disabled,
  aiUpdatedFields,
}: {
  effectiveType: 'task' | 'journal' | null
  onToggle: () => void
  disabled: boolean
  aiUpdatedFields: Set<string>
}) {
  if (!effectiveType) return null

  const { icon: Icon, label } = ENTRY_PRESENTATION[effectiveType]

  return (
    <FlashWrapper fieldKey="type" aiUpdatedFields={aiUpdatedFields}>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={`Entry type: ${label}. Click to toggle.`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
          'border-accent/40 bg-accent/10 text-accent-foreground hover:bg-accent/20',
          FOCUS_STYLES.ringCompact,
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <Icon className={ICON_SIZES.compact} weight="duotone" />
        {label}
      </button>
    </FlashWrapper>
  )
}

/**
 * ImportanceChip — toggle pill for task importance.
 */
function ImportanceChip({
  important,
  onChange,
  disabled,
  aiUpdatedFields,
}: {
  important: boolean
  onChange: (value: boolean) => void
  disabled: boolean
  aiUpdatedFields: Set<string>
}) {
  return (
    <FlashWrapper fieldKey="important" aiUpdatedFields={aiUpdatedFields}>
      <button
        type="button"
        onClick={() => onChange(!important)}
        disabled={disabled}
        aria-label={important ? 'Remove importance' : 'Mark as important'}
        aria-pressed={important}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
          FOCUS_STYLES.ringCompact,
          important
            ? 'border-destructive/40 bg-destructive/15 text-destructive hover:bg-destructive/20 font-medium'
            : 'border-border text-muted-foreground hover:border-border/80 hover:text-foreground',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <WarningCircleIcon
          className={ICON_SIZES.compact}
          weight={important ? 'fill' : 'regular'}
        />
        {important ? 'Important' : '!'}
      </button>
    </FlashWrapper>
  )
}

/**
 * CaptureChipsRow — post-analysis categorization chips.
 * Displayed after AI analysis with AnimatePresence fade-in.
 * Reuses existing selector components (TopicSubtitleSelector, MultiSelectProperty,
 * InlinePropertyPopover, DateField) with chip-styled triggers.
 */
export function CaptureChipsRow({
  effectiveType,
  onTypeToggle,
  dueDate,
  onDueDateChange,
  important,
  onImportantChange,
  selectedTopicIds,
  availableTopics,
  onTopicsChange,
  onAddTopic,
  onCreateTopic,
  selectedPeople,
  availablePeople,
  onAddPerson,
  onRemovePerson,
  isPeopleLoading,
  onAddNewPerson,
  suggestedPeople,
  addingPerson,
  onAddSuggestedPerson,
  onDismissSuggestedPerson,
  selectedProject,
  projectOptions,
  onProjectChange,
  projectLocked,
  disabled,
  aiUpdatedFields,
  show,
}: CaptureChipsRowProps) {
  const dueDateObj = dueDate ? new Date(dueDate) : undefined

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.15 }}
          className="flex flex-wrap items-center gap-1.5 px-1"
        >
          {/* 1. Type chip */}
          <TypeChip
            effectiveType={effectiveType}
            onToggle={onTypeToggle}
            disabled={disabled}
            aiUpdatedFields={aiUpdatedFields}
          />

          {/* 2. Topic chips — reuses TopicSubtitleSelector with chip trigger styling */}
          <FlashWrapper fieldKey="topics" aiUpdatedFields={aiUpdatedFields}>
            <TopicSubtitleSelector
              selectedTopicIds={selectedTopicIds}
              availableTopics={availableTopics}
              onChange={onTopicsChange}
              onAddNew={onAddTopic}
              onCreateTopic={onCreateTopic}
              disabled={disabled}
            />
          </FlashWrapper>

          {/* 3. People chips — reuses MultiSelectProperty */}
          <FlashWrapper fieldKey="people" aiUpdatedFields={aiUpdatedFields}>
            <MultiSelectProperty
              label="People"
              Icon={PROPERTY_ICONS.people}
              selectedItems={selectedPeople}
              availableItems={availablePeople}
              onAdd={onAddPerson}
              onRemove={onRemovePerson}
              getItemLabel={(p) => p.title}
              disabled={disabled}
              isLoading={isPeopleLoading}
              onAddNew={onAddNewPerson}
              addNewLabel="Add new..."
            />
          </FlashWrapper>

          {/* 4. Suggested people chips */}
          <SuggestedPeopleChips
            suggestions={suggestedPeople}
            addingPerson={addingPerson}
            isDisabled={disabled}
            onAdd={onAddSuggestedPerson}
            onDismiss={onDismissSuggestedPerson}
          />

          {/* 5. Project chip — static badge if locked, popover otherwise */}
          {projectLocked && selectedProject ? (
            <span className="text-muted-foreground border-border inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
              <PROPERTY_ICONS.project className={ICON_SIZES.compact} />
              {selectedProject.name}
            </span>
          ) : projectOptions.length > 0 ? (
            <FlashWrapper fieldKey="project" aiUpdatedFields={aiUpdatedFields}>
              <InlinePropertyPopover
                value={selectedProject?.id ?? null}
                valueLabel={selectedProject?.name}
                placeholder="+ project"
                options={projectOptions}
                onChange={onProjectChange}
                icon={<PROPERTY_ICONS.project className={ICON_SIZES.compact} />}
                allowClear
                disabled={disabled}
                className="border-border rounded-full border px-2 py-0.5 text-xs"
              />
            </FlashWrapper>
          ) : null}

          {/* 6. Due date chip — task only */}
          {effectiveType === 'task' && (
            <FlashWrapper fieldKey="dueDate" aiUpdatedFields={aiUpdatedFields}>
              <DateField
                value={dueDateObj}
                onChange={onDueDateChange}
                disabled={disabled}
                formatDate={(d) => format(d, 'MMM d')}
                placeholder="+ due"
                clearable={!!dueDateObj}
                variant="inline"
                className="border-border rounded-full border px-2 py-0.5 text-xs"
              />
            </FlashWrapper>
          )}

          {/* 7. Importance chip — task only */}
          {effectiveType === 'task' && (
            <ImportanceChip
              important={important}
              onChange={onImportantChange}
              disabled={disabled}
              aiUpdatedFields={aiUpdatedFields}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
